import React from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { axiospost } from "../../helpers/httprequest";
import { appHost } from "../../helpers/uri";

const Activate = () => {
  const [loading, setloading] = React.useState(true);
  const [error, seterror] = React.useState(undefined);
  const [success, setSuccess] = React.useState(false);
  const { id } = useParams();

  React.useEffect(() => {
    (async () => {
      try {
        const req = await axiospost(`ms_applicant/activate`, { id });
        if (req.data?.type !== "success") {
          throw new Error(req.data?.message);
        }
        setSuccess(true);
        setloading(false);
        // Redirect after 3 seconds to let user see success message
        setTimeout(() => {
          window.location.href = appHost;
        }, 3000);
      } catch ({ message }) {
        seterror(message);
        setloading(false);
      }
    })();
    //eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Gradient Overlay */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/20 z-10" />
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/images/college-bg.jpg')",
            backgroundBlendMode: "overlay",
          }}
        />
        <div className="relative z-20 flex flex-col justify-center px-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold mb-4">Account Activation</h1>
            <p className="text-xl text-white/90 mb-8">
              Verify your account to get started
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">Secure Verification</h3>
                  <p className="text-white/80 text-sm">Your account is being verified securely</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">Quick Process</h3>
                  <p className="text-white/80 text-sm">Activation happens automatically</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold">Ready to Apply</h3>
                  <p className="text-white/80 text-sm">Start your admission journey immediately</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Activation Status */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mb-8"
          >
            <img
              src="/images/college-logo.png"
              alt="ATBCOE Logo"
              className="h-32 mx-auto mb-4 object-contain"
            />
            <h2 className="text-2xl font-bold text-neutral-900 mb-2">
              Account Activation
            </h2>
            <p className="text-neutral-600">
              Adamu Tafawa Balewa College of Education, Kangere
            </p>
            <p className="text-sm text-neutral-500 mt-1">
              Admission Application Portal
            </p>
          </motion.div>

          {/* Activation Status Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            {/* Loading State */}
            {loading && (
              <div className="text-center py-8">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="inline-block mb-6"
                >
                  <svg
                    className="w-16 h-16 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </motion.div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                  Activating Your Account
                </h3>
                <p className="text-neutral-600">
                  Please wait while we verify and activate your account...
                </p>
                <div className="mt-6">
                  <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
                    <motion.div
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="h-full bg-primary-600 rounded-full"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Success State */}
            {!loading && success && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2
                  }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6"
                >
                  <svg
                    className="w-10 h-10 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </motion.div>
                <h3 className="text-2xl font-bold text-green-600 mb-3">
                  Activation Successful!
                </h3>
                <p className="text-neutral-700 mb-2">
                  Your account has been successfully activated.
                </p>
                <p className="text-neutral-600 text-sm">
                  Redirecting you to the login page...
                </p>
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-neutral-500">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </motion.div>
                  <span>Please wait...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className="text-center py-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2
                  }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6"
                >
                  <svg
                    className="w-10 h-10 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </motion.div>
                <h3 className="text-2xl font-bold text-red-600 mb-3">
                  Activation Failed
                </h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <p className="text-red-800 text-sm">
                    {error || "An error occurred while activating your account"}
                  </p>
                </div>
                <div className="space-y-4">
                  <p className="text-neutral-600 text-sm">
                    This could happen if:
                  </p>
                  <ul className="text-left text-neutral-600 text-sm space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>The activation link has expired</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>The account has already been activated</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>The activation link is invalid</span>
                    </li>
                  </ul>
                </div>
                <div className="mt-8">
                  <a
                    href={appHost}
                    className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    Go to Login Page
                  </a>
                </div>
                <p className="text-neutral-500 text-sm mt-4">
                  Need help? Contact support for assistance
                </p>
              </div>
            )}
          </motion.div>

          {/* Footer Note */}
          {!loading && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center text-sm text-neutral-500 mt-6"
            >
              If you continue to experience issues, please{" "}
              <a href="#" className="text-primary-600 hover:underline">
                contact support
              </a>
            </motion.p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Activate;
